import { useState, useEffect } from "react";
import { db } from "./firebase";
import { collection, getDocs, query, orderBy, limit, doc, updateDoc, writeBatch } from "firebase/firestore";

export default function Notifications({ user, onBack, onNavigate }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    loadNotifications();
  }, []);

  async function loadNotifications() {
    setLoading(true);
    try {
      const q = query(
        collection(db, "users", user.uid, "notifications"),
        orderBy("createdAt", "desc"),
        limit(50)
      );
      const snap = await getDocs(q);
      const notifs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setNotifications(notifs);
      setUnreadCount(notifs.filter(n => !n.read).length);
    } catch {}
    setLoading(false);
  }

  async function markAllRead() {
    try {
      const batch = writeBatch(db);
      notifications.filter(n => !n.read).forEach(n => {
        batch.update(doc(db, "users", user.uid, "notifications", n.id), { read: true });
      });
      await batch.commit();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {}
  }

  async function markRead(notifId) {
    try {
      await updateDoc(doc(db, "users", user.uid, "notifications", notifId), { read: true });
      setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  }

  function timeAgo(dateStr) {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  }

  function getNotifIcon(type) {
    const icons = {
      like: "❤️", comment: "💬", follow: "👤", approve: "✅",
      reject: "❌", newSong: "🎵", mention: "📣", welcome: "🎉"
    };
    return icons[type] || "🔔";
  }

  function getNotifColor(type) {
    const colors = {
      like: "#e91429", comment: "#4776e6", follow: "#1db954",
      approve: "#1db954", reject: "#e91429", newSong: "#f59e0b",
      mention: "#8b5cf6", welcome: "#1db954"
    };
    return colors[type] || "#555";
  }

  // Demo notifications if empty
  const demoNotifs = [
    { id: "d1", type: "welcome", title: "Welcome to MyMusic! 🎉", message: "Start exploring music, upload your songs and connect with artists worldwide!", createdAt: new Date().toISOString(), read: false },
    { id: "d2", type: "newSong", title: "New feature available!", message: "You can now upload your original music in Artist Profile!", createdAt: new Date(Date.now() - 3600000).toISOString(), read: false },
  ];

  const displayNotifs = notifications.length > 0 ? notifications : demoNotifs;

  return (
   <div style={{ position: "fixed", inset: 0, zIndex: 200, background: "#000", color: "#fff", fontFamily: "'DM Sans',sans-serif", overflowY: "auto" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@400;500;600&display=swap'); @keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 16px", borderBottom: "1px solid #111" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={onBack} style={{ background: "#111", border: "1px solid #222", color: "#fff", width: 34, height: 34, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
            <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: "none", stroke: "#fff", strokeWidth: 2 }}><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <div>
            <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 20, fontWeight: 800 }}>
              🔔 Notifications
              {unreadCount > 0 && <span style={{ marginLeft: 8, background: "#e91429", color: "#fff", fontSize: 11, fontWeight: 700, padding: "2px 7px", borderRadius: 50 }}>{unreadCount}</span>}
            </div>
          </div>
        </div>
        {unreadCount > 0 && (
          <button onClick={markAllRead} style={{ background: "none", border: "1px solid #2a2a2a", color: "#1db954", fontSize: 12, padding: "6px 12px", borderRadius: 50, cursor: "pointer" }}>
            Mark all read
          </button>
        )}
      </div>

      {/* Notifications list */}
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}>
          <div style={{ width: 32, height: 32, border: "3px solid #222", borderTopColor: "#1db954", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        </div>
      ) : displayNotifs.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 20px" }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>🔔</div>
          <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 18, fontWeight: 800, marginBottom: 8 }}>No notifications yet</div>
          <div style={{ fontSize: 13, color: "#555" }}>When someone likes your song or follows you, you'll see it here!</div>
        </div>
      ) : displayNotifs.map(notif => (
       <div key={notif.id} onClick={() => {
            markRead(notif.id);
            if (notif.songId && (notif.type === "like" || notif.type === "comment" || notif.type === "mention")) {
              onNavigate && onNavigate("feed", notif.songId);
            } else if (notif.type === "follow" && notif.fromUserId) {
              onNavigate && onNavigate("profile", notif.fromUserId);
            }
          }}
          style={{ display: "flex", alignItems: "flex-start", gap: 14, padding: "14px 16px", borderBottom: "1px solid #0a0a0a", background: notif.read ? "transparent" : "rgba(29,185,84,0.04)", cursor: "pointer" }}>
          
          {/* Icon */}
          <div style={{ width: 44, height: 44, borderRadius: "50%", background: `${getNotifColor(notif.type)}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>
            {notif.userPhoto ? (
              <img src={notif.userPhoto} alt="" style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }} />
            ) : getNotifIcon(notif.type)}
          </div>

          {/* Content */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: notif.read ? 400 : 600, color: notif.read ? "#ccc" : "#fff", lineHeight: 1.4, marginBottom: 4 }}>
              {notif.title}
            </div>
            {notif.message && (
              <div style={{ fontSize: 12, color: "#555", lineHeight: 1.5, marginBottom: 4 }}>{notif.message}</div>
            )}
            <div style={{ fontSize: 11, color: "#333" }}>{timeAgo(notif.createdAt)}</div>
          </div>

          {/* Unread dot */}
          {!notif.read && (
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#1db954", flexShrink: 0, marginTop: 6 }} />
          )}
        </div>
      ))}
    </div>
  );
}

// Helper function to send notifications - use this from other components
export async function sendNotification(db, toUserId, notification) {
  try {
    const { addDoc, collection } = await import("firebase/firestore");
    await addDoc(collection(db, "users", toUserId, "notifications"), {
      ...notification,
      read: false,
      createdAt: new Date().toISOString(),
    });
  } catch {}
}
